import React, { useState, useCallback } from 'react';
import { stagePublicAPI } from '../services/api';
import { tailleLisible, dateFr } from './StageDepotPublic';
import './StagePublic.css';

/**
 * CERTIFICATION — la PAGE DU MAÎTRE (lot C.3)
 * ============================================
 * Spec     : doc/modules/suivi_de_stage_certification.md § Pages publiques — le maître certifie
 * Maquette : #tab-certifmaitre
 *
 * Le maître n'a pas de compte et ne s'identifie pas (E56) : le lien est la porte.
 * Une décision PAR DOCUMENT (C3) : « Certifier » — toutes les attestations demandées pour
 * CE livrable (CM1) — ou « Ne peut pas certifier » + motif, transmis à l'étudiant.
 * « Lire le document » ouvre le PDF dans un nouvel onglet (C22) ; la première ouverture
 * est horodatée côté serveur (CM2). Signataire et fonction pré-remplis, modifiables (CM4).
 * Bleu encre (C36) : c'est une page TRANSMISE.
 *
 * ⚠️ `apercu` = une page FICTIVE (« Voir la page du maître », C28) : aucun appel serveur.
 */

/** La même règle que le serveur — l'écran l'anticipe, le serveur décide. */
function raisonNonValidable(docs, decisions, signataire) {
    for (const d of docs.filter((x) => x.type === 'a_decider')) {
        const x = decisions[d.depot_id];
        if (!x?.decision) return `Il manque une décision pour « ${d.libelle} ».`;
        if (x.decision === 'certifie' && d.attestations.some((a) => !x.cases?.[a.cle])) {
            return `Pour certifier « ${d.libelle} », cochez toutes les attestations — sinon choisissez « Ne peut pas certifier ».`;
        }
        if (x.decision === 'non_certifie' && !String(x.motif || '').trim()) return `Indiquez le motif pour « ${d.libelle} ».`;
    }
    if (!String(signataire.nom || '').trim()) return 'Indiquez le nom du signataire.';
    return null;
}

function StageCertifMaitrePublic({ token, page, apercu = false }) {
    const [decisions, setDecisions] = useState({});       // depot_id → { decision, cases, motif }
    const [signataire, setSignataire] = useState({ nom: page.signataire?.nom || '', fonction: page.signataire?.fonction || '' });
    const [recap, setRecap] = useState(false);
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [lus, setLus] = useState({});
    // Si le navigateur bloque le nouvel onglet : un LIEN à cliquer, la page reste en place.
    const [liens, setLiens] = useState({});
    const [envoye, setEnvoye] = useState(null);
    const docs = page.documents || [];
    const aDecider = docs.filter((x) => x.type === 'a_decider');

    const poser = (id, morceau) => { setRecap(false); setDecisions((x) => ({ ...x, [id]: { ...(x[id] || {}), ...morceau } })); };
    const cocher = (id, cle, v) => { setRecap(false); setDecisions((x) => ({ ...x, [id]: { ...(x[id] || {}), cases: { ...(x[id]?.cases || {}), [cle]: v } } })); };

    /**
     * Ouvrir le PDF dans un NOUVEL ONGLET. ⚠️ L'onglet est ouvert AVANT l'appel réseau :
     * ouvert après, il serait bloqué comme une fenêtre surgissante.
     * ⚠️ S'il est bloqué malgré tout : JAMAIS remplacer la page par le PDF (défaut vu au banc du
     * lot C.3 — le maître perdait les décisions déjà cochées). On propose un lien à cliquer.
     */
    const lire = useCallback(async (doc) => {
        if (apercu) return;
        setErreur(null);
        const onglet = window.open('', '_blank');
        try {
            const r = await stagePublicAPI.lireDocumentCertif(token, doc.depot_id);
            const url = URL.createObjectURL(r.data);
            if (onglet) onglet.location.href = url;
            else setLiens((x) => ({ ...x, [doc.depot_id]: url }));
            setLus((x) => ({ ...x, [doc.depot_id]: true }));
            setTimeout(() => URL.revokeObjectURL(url), 600000);
        } catch (e) {
            if (onglet) onglet.close();
            setErreur("Le document n'a pas pu être ouvert. Réessayez, ou contactez le secrétariat.");
        }
    }, [apercu, token]);

    const valider = useCallback(async () => {
        if (apercu) { setEnvoye("Aperçu : rien n'a été envoyé. Dans la vraie page, le maître verrait ici le remerciement."); return; }
        setEnCours(true); setErreur(null);
        try {
            const r = await stagePublicAPI.certifierMaitre(token, {
                decisions: aDecider.map((d) => ({ depot_id: d.depot_id, decision: decisions[d.depot_id]?.decision,
                    cases: decisions[d.depot_id]?.cases || {}, motif: decisions[d.depot_id]?.motif || '' })),
                signature_nom: signataire.nom, signature_fonction: signataire.fonction
            });
            setEnvoye(r.data.message);
        } catch (e) {
            setRecap(false);
            setErreur(e.response?.data?.error || "L'envoi n'a pas abouti. Réessayez.");
        } finally { setEnCours(false); }
    }, [apercu, token, aDecider, decisions, signataire]);

    const raison = raisonNonValidable(docs, decisions, signataire);
    const e = page.entete || {};

    if (envoye) {
        return (
            <div className="pub-page pub-certif">
                <div className="pub-message pub-message-succes">
                    <i className="fa-solid fa-circle-check" />
                    <h2>Certification enregistrée</h2>
                    <p>{envoye}</p>
                    <p className="pub-discret">Vous pouvez fermer cette page.</p>
                </div>
            </div>
        );
    }

    let n = 0;
    return (
        <div className="pub-page pub-certif">
            <div className="form-public">
                <div className="form-public-header">
                    <h2><i className="fa-solid fa-stamp" /> Certification des documents — {e.formation_nom}</h2>
                    <p>Demandée par {e.demandeur || 'le secrétariat des stages'} — {e.departement_nom}</p>
                </div>
                <div className="form-public-body">
                    {apercu && (
                        <div className="pub-encart pub-encart-info">
                            <i className="fa-solid fa-eye" /> Aperçu — données fictives. Cette page est celle que reçoit le maître ; rien n'est envoyé.
                        </div>
                    )}
                    {page.texte_intro && <div className="pub-bandeau-certif">{page.texte_intro}</div>}

                    <div className="form-section">
                        <h3><i className="fa-solid fa-user-graduate" /> Étudiant</h3>
                        <div className="form-row">
                            <div className="form-group"><label>Nom et prénom</label><input value={`${page.etudiant?.nom || ''} ${page.etudiant?.prenom || ''}`} readOnly /></div>
                            <div className="form-group"><label>Entreprise</label><input value={page.etudiant?.entreprise || ''} readOnly /></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Processus</label><input value={page.etudiant?.processus || ''} readOnly /></div>
                            <div className="form-group"><label>Période d'activité</label><input value={page.etudiant?.periode || ''} readOnly /></div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3><i className="fa-solid fa-file-lines" /> Les documents — une décision pour chacun</h3>
                        {docs.map((d) => {
                            n += 1;
                            if (d.type === 'declaration') {
                                return (
                                    <div key={d.livrable_id} className="pub-certif-doc pub-certif-doc-info">
                                        <div className="pub-certif-doc-titre">
                                            <i className="fa-solid fa-graduation-cap" /> {n}. {d.libelle}
                                            <span className="pub-badge attente">Hors entreprise — rien à certifier</span>
                                        </div>
                                        <div style={{ fontSize: 13 }}>
                                            L'étudiant déclare avoir traité <b>le sujet proposé par la formation</b>, et non une mission de l'entreprise :
                                            {' '}<i>« {d.precision} »</i>.
                                        </div>
                                        <div className="pub-discret" style={{ marginTop: 4 }}>
                                            <i className="fa-solid fa-circle-info" /> Si cette déclaration vous paraît inexacte, merci de le signaler au secrétariat en répondant au courriel.
                                        </div>
                                    </div>
                                );
                            }
                            if (d.type === 'certifie') {
                                return (
                                    <div key={d.livrable_id} className="pub-certif-doc pub-certif-doc-info">
                                        <div className="pub-certif-doc-titre">
                                            <i className="fa-solid fa-circle-check" style={{ color: '#15803d' }} /> {n}. {d.libelle}
                                            <span className="pub-badge ok"><i className="fa-solid fa-check" /> Déjà certifié</span>
                                        </div>
                                        <div className="pub-discret">{d.nom_fichier}</div>
                                    </div>
                                );
                            }
                            const x = decisions[d.depot_id] || {};
                            return (
                                <div key={d.livrable_id} className={'pub-certif-doc' + (x.decision === 'non_certifie' ? ' non' : '')}>
                                    <div className="pub-certif-doc-titre">
                                        <i className="fa-solid fa-file-pdf" style={{ color: '#dc2626' }} /> {n}. {d.libelle}
                                        <span className="pub-discret" style={{ fontWeight: 400 }}>{d.nom_fichier} · {tailleLisible(d.taille_octets)}</span>
                                        <button type="button" className="pub-btn pub-btn-secondaire pub-btn-petit" disabled={apercu}
                                                title={apercu ? 'Aperçu : pas de document' : 'Ouvre le document dans un nouvel onglet'}
                                                onClick={() => lire(d)}>
                                            <i className="fa-solid fa-eye" /> Lire le document
                                        </button>
                                    </div>
                                    {liens[d.depot_id] && (
                                        <div className="pub-encart pub-encart-info" style={{ marginBottom: 8 }}>
                                            <i className="fa-solid fa-circle-info" /> Votre navigateur a bloqué le nouvel onglet :
                                            {' '}<a href={liens[d.depot_id]} target="_blank" rel="noopener noreferrer">ouvrir le document</a>.
                                        </div>
                                    )}
                                    {(d.ouvert_le || lus[d.depot_id]) && (
                                        <div className="pub-discret" style={{ marginBottom: 8 }}>
                                            <i className="fa-solid fa-check" /> Ouvert{d.ouvert_le ? ` le ${dateFr(d.ouvert_le)}` : ''}
                                        </div>
                                    )}
                                    <div className="pub-certif-choix">
                                        <label className={x.decision === 'certifie' ? 'on-ok' : ''}>
                                            <input type="radio" name={'dec-' + d.depot_id} checked={x.decision === 'certifie'}
                                                   onChange={() => poser(d.depot_id, { decision: 'certifie' })} />
                                            <i className="fa-solid fa-circle-check" /> Certifier
                                        </label>
                                        <label className={x.decision === 'non_certifie' ? 'on-non' : ''}>
                                            <input type="radio" name={'dec-' + d.depot_id} checked={x.decision === 'non_certifie'}
                                                   onChange={() => poser(d.depot_id, { decision: 'non_certifie' })} />
                                            <i className="fa-solid fa-circle-xmark" /> Ne peut pas certifier
                                        </label>
                                    </div>
                                    {x.decision === 'certifie' && (
                                        <div className="pub-certif-cases">
                                            {d.attestations.map((a) => (
                                                <label key={a.cle}>
                                                    <input type="checkbox" checked={!!x.cases?.[a.cle]}
                                                           onChange={(ev) => cocher(d.depot_id, a.cle, ev.target.checked)} /> {a.libelle}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                    {x.decision === 'non_certifie' && (
                                        <>
                                            <div className="pub-discret" style={{ marginBottom: 6 }}>
                                                Pour ce livrable, il vous est demandé : {d.attestations.map((a) => a.libelle).join(' · ')}
                                            </div>
                                            <div className="form-group" style={{ marginBottom: 0 }}>
                                                <label htmlFor={'motif-' + d.depot_id}>Motif <span className="form-required">*</span> <span className="pub-discret">— transmis à l'étudiant</span></label>
                                                <textarea id={'motif-' + d.depot_id} rows={3} maxLength={2000} value={x.motif || ''}
                                                          onChange={(ev) => poser(d.depot_id, { motif: ev.target.value })} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="form-section">
                        <h3><i className="fa-solid fa-signature" /> Signature</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="cm-nom">Nom du signataire <span className="form-required">*</span></label>
                                <input id="cm-nom" maxLength={200} value={signataire.nom} onChange={(ev) => { setRecap(false); setSignataire({ ...signataire, nom: ev.target.value }); }} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="cm-fonction">Fonction</label>
                                <input id="cm-fonction" maxLength={200} value={signataire.fonction} onChange={(ev) => { setRecap(false); setSignataire({ ...signataire, fonction: ev.target.value }); }} />
                            </div>
                        </div>
                    </div>

                    {recap && !raison && (
                        <div className="pub-recap">
                            <b>Vous allez valider votre certification.</b> Elle ne sera plus modifiable.
                            <ul>
                                {aDecider.map((d) => (
                                    <li key={d.depot_id}>{d.libelle} : <b>{decisions[d.depot_id]?.decision === 'certifie' ? 'certifié' : 'ne peut pas être certifié'}</b>
                                        {decisions[d.depot_id]?.decision === 'non_certifie' ? ` — « ${decisions[d.depot_id].motif} »` : ''}</li>
                                ))}
                            </ul>
                            <div style={{ marginTop: 6 }}>Signé : <b>{signataire.nom}</b>{signataire.fonction ? `, ${signataire.fonction}` : ''}</div>
                        </div>
                    )}
                    {erreur && <div className="pub-encart pub-encart-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
                </div>

                <div className="form-public-footer pub-pied-depot">
                    {raison && <div className="pub-aide pub-raison"><i className="fa-solid fa-circle-info" /> {raison}</div>}
                    {recap && !raison ? (
                        <>
                            <button type="button" className="pub-btn pub-btn-secondaire" disabled={enCours} onClick={() => setRecap(false)}>Revenir</button>
                            <button type="button" className="pub-btn" disabled={enCours} onClick={valider}>
                                {enCours ? <><i className="fa-solid fa-circle-notch pub-tourne" /> Envoi…</>
                                         : <><i className="fa-solid fa-check" /> Confirmer ma certification</>}
                            </button>
                        </>
                    ) : (
                        <button type="button" className="pub-btn" disabled={enCours || !!raison} onClick={() => setRecap(true)}>
                            <i className="fa-solid fa-check" /> Valider ma certification
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default StageCertifMaitrePublic;
